import { Check, Zap } from 'lucide-react'
import { useId, useMemo, useReducer, useState, type RefObject } from 'react'
import { Icon } from '../../platform/icons/Icon'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useAccounts } from '../accounts/useAccounts'
import { buildTree, CATEGORY_TYPE_LABEL } from '../categories/categoryTree'
import { useCategories } from '../categories/useCategories'
import { needsRenumber, priorityFor, reduce } from './builderState'
import { clampStart, earliestAllowed, localToday, longDate, windowStart } from './backfillWindow'
import { RuleActionRow, type CategorySegment } from './RuleActionRow'
import { RuleConditionRow, type Choice } from './RuleConditionRow'
import { QuietIconButton, useControlFloor } from './ruleControls'
import { conditionProblem, isSavable, MAX_ACTIONS, toWrite, type FieldKey, type RuleDraft } from './ruleWire'
import { RulePreview } from './RulePreview'
import { SaveError, saveFailureCopy } from './useSaveRule'
import './rules.css'

type Place = 'top' | 'bottom'

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const letter = (g: number) => LETTERS[g] ?? String(g + 1)


const SUBHEADING = {
  new: 'Tell TrackE5 how to file transactions so you never have to do it by hand.',
  edit: 'Changes apply to new transactions as soon as you save.',
  editPast: 'Changes apply to new transactions, and to past ones once you confirm.',
}

const humanType = (t: string) => (t === '' ? t : t.charAt(0).toUpperCase() + t.slice(1))

function useChoices(): {
  categories: CategorySegment[]
  select: Partial<Record<FieldKey, Choice[]>>
} {
  const cats = useCategories()
  const accs = useAccounts()
  const catData = cats.status === 'success' || cats.status === 'stale' ? cats.data : undefined
  const accData = accs.status === 'success' || accs.status === 'stale' ? accs.data : undefined
  const categories = useMemo(
    () =>
      buildTree(catData ?? []).map((s) => ({
        label: CATEGORY_TYPE_LABEL[s.type],
        options: s.nodes.flatMap((n) => [
          { id: n.category.id, name: n.category.name, depth: 0 as const },
          ...n.children.map((c) => ({ id: c.id, name: c.name, depth: 1 as const })),
        ]),
      })),
    [catData],
  )
  const select = useMemo(() => {
    const list = accData ?? []
    const types = [...new Set(list.map((a) => a.type))].sort()
    return {
      account_id: list.map((a) => ({ v: a.id, l: a.mask ? `${a.name} ••${a.mask}` : a.name })),
      account_type: types.map((t) => ({ v: t, l: humanType(t) })),
    }
  }, [accData])
  return { categories, select }
}

/**
 * The rule builder — a form in an `Overlay`, which owns the focus trap,
 * Escape and focus restore. A sheet on a phone (M11-R23), 880px wide above.
 *
 * Groups are ORed, as the API evaluates `conditions`: there is no top-level
 * AND/OR toggle because the wire has nowhere to put one. Each group's own
 * All/Any is its `logic_operator`.
 */
export function RuleBuilder({
  initial,
  priorities = [],
  place: initialPlace = 'top',
  onClose,
  onSubmit,
  onDelete,
  progress,
  busy = false,
  notice,
  restoreFocusTo,
}: {
  initial: RuleDraft
  /** Every existing rule's priority; new rules are placed relative to them. */
  priorities?: readonly number[]
  place?: Place
  onClose: () => void
  onSubmit: (draft: RuleDraft, extra: { priority?: number; renumber?: boolean }) => Promise<void>
  /** Edit only. Task 10 renders the confirmation. */
  onDelete?: () => void
  /** What the save is doing past "saving" — the dry-run's count (M11-R11). */
  progress?: { button: string; status: string }
  /** A step of this builder's save running outside it (the confirmed flip).
   *  The builder's own save and count are covered by `pending`. */
  busy?: boolean
  /** Why the opener changed the prefill — shown above the actions. */
  notice?: string
  /** Forwarded to the Overlay: where focus goes if the opener is gone on close. */
  restoreFocusTo?: RefObject<HTMLElement | null>
}) {
  // A stored start date can be older than today's 365-day floor — it was
  // valid when saved, and the floor moves daily. Left as is, it sits below the
  // date input's `min` and the form never submits. The server clamps silently
  // too, and backfill_start_date alone never triggers a backfill, so clamping
  // it on open changes no history.
  const [draft, dispatch] = useReducer(reduce, initial, (d) => ({
    ...d,
    backfillStart: clampStart(d.backfillStart, localToday()),
  }))
  const [place, setPlace] = useState<Place>(initialPlace)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [today] = useState(localToday)
  const { categories, select } = useChoices()
  const h = useControlFloor()
  const rowFloor = useHitTarget('page')
  const phone = useBreakpoint() === 'phone'
  const hintId = useId()
  const nameId = useId()
  const pastHintId = useId()
  const noticeId = useId()
  // The opener's explanation stands until the user has chosen a category.
  const showNotice =
    notice !== undefined && draft.actions.some((a) => a.type === 'set_category' && a.value === '')
  const isNew = draft.id === null
  const savable = isSavable(draft)
  // An amount typed wrong or half-done blocks the save (#517); the hint points
  // at it rather than asking for a condition the person has already written.
  const problems = draft.groups.flatMap((g) => g.conditions.map(conditionProblem))
  const amountBlocked = problems.some((p) => p !== null)
  const onlyIncomplete = amountBlocked && problems.every((p) => p === null || p === 'amount_incomplete')
  const onlyMissing = amountBlocked && problems.every((p) => p === null || p === 'amount_missing')
  const multi = draft.groups.length > 1
  // Not while a save is in flight — final review C1. A close mid-save left the
  // save to finish into a builder that no longer existed. Every close path
  // (Escape, scrim, Cancel, ×) goes through `close`, as ConfirmDialog refuses
  // cancel while busy.
  const locked = pending || busy
  const close = () => {
    if (!locked) onClose()
  }

  async function save() {
    if (!savable || pending) return
    setPending(true)
    setError(null)
    try {
      await onSubmit(draft, isNew
          ? {
              priority: priorityFor(place, priorities),
              ...(needsRenumber(place, priorities) ? { renumber: true } : {}),
            }
          : {})
    } catch (e) {
      // A SaveError already carries the copy — including "saved, but not
      // applied", which only the save sequence knows.
      setError(e instanceof SaveError ? e.message : saveFailureCopy(e, toWrite(draft).name))
    } finally {
      setPending(false)
    }
  }

  const subheading = isNew ? SUBHEADING.new : draft.applyToPast ? SUBHEADING.editPast : SUBHEADING.edit

  return (
    <Overlay
      open
      onClose={close}
      placement="sheet"
      size="wide"
      label={isNew ? 'New rule' : 'Edit rule'}
      restoreFocusTo={restoreFocusTo}
    >
      <form
        className={phone ? 'rule-builder rule-builder--phone' : 'rule-builder'}
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        {phone ? (
          // M11-R23: the sheet's own top bar — Cancel / heading / Save. Save is
          // the SAME submit and carries the footer button's disabled state; the
          // footer's pair is not rendered on a phone, so nothing is named twice.
          <>
            <div className="rule-builder__bar">
              <Button
                variant="outline"
                size="panel"
                className="rule-builder__bar-cancel"
                aria-disabled={locked}
                onClick={close}
              >
                Cancel
              </Button>
              <h3 className="rule-builder__title rule-builder__bar-title">{isNew ? 'New rule' : 'Edit rule'}</h3>
              <Button
                type="submit"
                variant="primary"
                size="panel"
                aria-disabled={!savable || pending}
                aria-describedby={hintId}
              >
                {pending ? (progress?.button ?? 'Saving…') : 'Save'}
              </Button>
            </div>
            <p className="rule-builder__sub">{subheading}</p>
          </>
        ) : (
          <div className="rule-builder__head">
            {/* Non-fill lime (R17): the dialog's one lime FILL is its primary action. */}
            <span className="rule-builder__tile" aria-hidden="true">
              <Icon glyph={Zap} size={19} />
            </span>
            <span className="rule-builder__headings">
              <h3 className="rule-builder__title">{isNew ? 'New rule' : 'Edit rule'}</h3>
              <p className="rule-builder__sub">{subheading}</p>
            </span>
            <QuietIconButton aria-label="Close" blocked={locked} onClick={close} />
          </div>
        )}

        <div className="rule-builder__name-row">
          <div className="rule-builder__name">
            <label className="rule-builder__label" htmlFor={nameId}>
              Rule name
            </label>
            <input
              id={nameId}
              className="rule-control rule-control--name"
              style={{ minHeight: h }}
              value={draft.name}
              placeholder="e.g. Coffee runs"
              onChange={(e) => dispatch({ t: 'name', v: e.target.value })}
            />
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={draft.enabled}
            className={`rule-row__switch rule-builder__active${draft.enabled ? ' rule-row__switch--on' : ''}`}
            onClick={() => dispatch({ t: 'enabled', v: !draft.enabled })}
          >
            <span className="rule-row__switch-track" aria-hidden="true">
              <span className="rule-row__switch-thumb" />
            </span>
            <span className="rule-builder__switch-label">Active</span>
          </button>
        </div>

        <section className="rule-builder__section" aria-label="When">
          <p className="rule-builder__lead">
            <span className="rule-builder__tag">When</span>
            {multi ? 'a transaction matches any group below' : 'a transaction matches'}
          </p>
          {draft.groups.map((group, g) => {
            const L = letter(g)
            return (
              // Index keys, knowingly: `RuleDraft` (ruleWire's contract) gives
              // groups, conditions and actions no id, and position IS the
              // identity every label here speaks ("Group B, condition 2").
              // Every control is controlled, so a reused node shows the new value.
              <div key={g} className="rule-group-wrap">
                {g > 0 && (
                  <p className="rule-group__or" aria-hidden="true">
                    OR
                  </p>
                )}
                <div className="rule-group" role="group" aria-label={multi ? `Group ${L}` : 'Conditions'}>
                  <div className="rule-group__head">
                    <span className="rule-group__title">{multi ? `Group ${L}` : 'Conditions'}</span>
                    <span className="rule-group__of">match</span>
                    <SegmentedControl
                      label={`Group ${L}, match`}
                      name={`rule-group-${g}-join`}
                      size="page"
                      tone="neutral"
                      options={[
                        { value: 'and', label: 'All' },
                        { value: 'or', label: 'Any' },
                      ]}
                      value={group.join}
                      onChange={(v) => dispatch({ t: 'groupJoin', g, v })}
                    />
                    <span className="rule-group__of">of these</span>
                    {multi && (
                      <Button
                        variant="outline"
                        size="panel"
                        className="rule-group__remove"
                        aria-label={`Remove group ${L}`}
                        onClick={() => dispatch({ t: 'removeGroup', g })}
                      >
                        Remove group
                      </Button>
                    )}
                  </div>
                  <div className="rule-group__body">
                  <ul className="rule-group__conds">
                    {group.conditions.map((cond, c) => (
                      <RuleConditionRow
                        key={c}
                        cond={cond}
                        g={g}
                        c={c}
                        prefix={`Group ${L}, condition ${c + 1}`}
                        removable={multi || group.conditions.length > 1}
                        choices={select[cond.field] ?? []}
                        dispatch={dispatch}
                      />
                    ))}
                  </ul>
                  <Button
                    variant="outline"
                    size="panel"
                    className="rule-add"
                    aria-label={`Add condition to group ${L}`}
                    onClick={() => dispatch({ t: 'addCond', g })}
                  >
                    ＋ Add condition
                  </Button>
                  </div>
                </div>
              </div>
            )
          })}
          <Button variant="chip" size="panel" onClick={() => dispatch({ t: 'addGroup' })}>
            ＋ Add condition group
          </Button>
        </section>

        <section className="rule-group" aria-label="Then">
          <div className="rule-group__head">
            <span className="rule-builder__tag rule-builder__tag--then">Then</span>
            <span className="rule-group__of">make these changes</span>
            <span className="rule-builder__count">
              {draft.actions.length} of {MAX_ACTIONS}
            </span>
          </div>
          <div className="rule-group__body">
          {showNotice && (
            <p id={noticeId} className="rule-builder__notice">
              {notice}
            </p>
          )}
          <ul className="rule-group__conds">
            {draft.actions.map((action, i) => (
              <RuleActionRow
                key={i}
                action={action}
                i={i}
                removable={draft.actions.length > 1}
                categories={categories}
                dispatch={dispatch}
                describedBy={showNotice ? noticeId : undefined}
              />
            ))}
          </ul>
          {draft.actions.length >= MAX_ACTIONS ? (
            <Button variant="outline" size="panel" className="rule-add" aria-disabled="true">
              A rule can make up to 5 changes
            </Button>
          ) : (
            <Button variant="outline" size="panel" className="rule-add" onClick={() => dispatch({ t: 'addAction' })}>
              ＋ Add action
            </Button>
          )}
          </div>
        </section>

        <section className="rule-group rule-past" aria-label="Past transactions">
          {/* R23: a checkbox, as designed. A real <input>, stretched invisibly
              over its label row so the whole row is the target; the box the
              user sees is `rule-past__box`, skinned from tokens — a native
              checkbox's own paint is invisible to every colour guard. */}
          {/* `page` (33px), not the fields' `panel` floor: a control with no
              `.btn` size class is held to the page floor by the hit-target sweep. */}
          <label className="rule-past__row" style={{ minHeight: rowFloor }}>
            <input
              type="checkbox"
              className="rule-past__input"
              checked={draft.applyToPast}
              aria-describedby={pastHintId}
              onChange={(e) => dispatch({ t: 'applyToPast', v: e.target.checked })}
            />
            <span className="rule-past__box" aria-hidden="true">
              <Icon glyph={Check} size={13} />
            </span>
            <span className="rule-past__title">Apply to past transactions</span>
          </label>
          <p id={pastHintId} className="rule-past__hint rule-past__hint--lead">
            Only fills transactions that have no category yet. Existing categories are never
            overwritten.
          </p>
          {draft.applyToPast && (
            <div className="rule-past__from">
              <label className="rule-past__label">
                Starting from
                <input
                  type="date"
                  className="rule-control"
                  style={{ minHeight: h }}
                  min={earliestAllowed(today)}
                  max={today}
                  value={draft.backfillStart ?? ''}
                  onChange={(e) => dispatch({ t: 'backfillStart', v: e.target.value || null })}
                />
              </label>
              {draft.backfillStart !== null && (
                <Button variant="outline" size="panel" onClick={() => dispatch({ t: 'backfillStart', v: null })}>
                  Use last 90 days
                </Button>
              )}
              <p className="rule-past__hint">
                {draft.backfillStart === null
                  ? `No date set — uses the last 90 days (since ${longDate(windowStart(null, today))}).`
                  : `From ${longDate(windowStart(draft.backfillStart, today))}.`}
              </p>
            </div>
          )}
        </section>

        <RulePreview draft={draft} />

        {isNew && (
          <div className="rule-builder__priority">
            {/* The group names itself to assistive tech; this is the same word, drawn. */}
            <span className="rule-builder__priority-label" aria-hidden="true">
              Priority
            </span>
            <SegmentedControl
              label="Priority"
              name="rule-priority"
              size="page"
              tone="neutral"
              options={[
                { value: 'top', label: 'Top · runs first' },
                { value: 'bottom', label: 'Bottom · runs last' },
              ]}
              value={place}
              onChange={setPlace}
            />
          </div>
        )}

        {error !== null && (
          <p className="rule-builder__error" role="alert">
            {error}
          </p>
        )}

        <div className="rule-builder__foot">
          {/* Task 10 renders the delete confirmation; this only asks for it. */}
          {!isNew && onDelete !== undefined && (
            <Button
              variant="destructive"
              size="panel"
              className="rule-builder__delete"
              aria-disabled={locked}
              onClick={() => {
                if (!locked) onDelete()
              }}
            >
              Delete rule
            </Button>
          )}
          <span
            id={hintId}
            className={
              savable || pending ? 'rule-builder__hint' : 'rule-builder__hint rule-builder__hint--blocked'
            }
            role="status"
          >
            {pending && progress !== undefined
              ? progress.status
              : savable
                ? 'Ready to save'
                : onlyIncomplete
                  ? 'Enter both amounts to save'
                  : onlyMissing
                  ? 'Enter an amount to save'
                  : amountBlocked
                  ? 'Fix the amount to save'
                  : 'Add a condition and an action'}
          </span>
          {!phone && (
            <>
              <Button variant="outline" size="page" aria-disabled={locked} onClick={close}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="page"
                aria-disabled={!savable || pending}
                aria-describedby={hintId}
              >
                {pending ? (progress?.button ?? 'Saving…') : isNew ? 'Create rule' : 'Save changes'}
              </Button>
            </>
          )}
        </div>
      </form>
    </Overlay>
  )
}
